import { useEffect, useMemo, useRef, useState, useCallback, forwardRef, useImperativeHandle, type MutableRefObject } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize, PictureInPicture2, RotateCcw, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useWatchTracker } from "@/hooks/useWatchTracker";
import { recordTickWithGapFill } from "@/hooks/watchTrackerLogic";
import { logInfo } from "@/services/eventLogService";
import playerjs from "player.js";

interface ChallengeVideoPlayerProps {
  videoUrl: string;
  thumbnailUrl?: string;
  onProgress?: (progress: number) => void;
  onPlay?: () => void;
  onComplete?: () => void;
  /**
   * The player reported the video reached its end (every such report, unlike
   * the once-only `onComplete`, which fires earlier at the watch threshold).
   * Best-effort: a looping clip or a failed player SDK never reports it.
   */
  onEnded?: () => void;
  className?: string;
}

export interface ChallengeVideoPlayerHandle {
  requestPiP: () => Promise<void>;
  isPlaying: () => boolean;
  /**
   * Best-effort pause of whichever player is mounted (native <video>, or the
   * YouTube / Vimeo / Livid iframe through its own command channel). A no-op
   * when nothing is mounted or the embed has not finished loading.
   */
  pause: () => void;
}

/** Slot each iframe player fills with its own pause command. */
type PauseSlot = MutableRefObject<(() => void) | null>;

// Hosted YouTube proxy. iOS WKWebView strips the Referer header when the host
// page is served by Capacitor's WKURLSchemeHandler, so YouTube's "enablejsapi
// + origin" check fails and the player shows "Error 153 — Video player
// configuration error". The fix is to embed via an iframe pointing at a real
// HTTPS domain that sends a normal Referer (here, sy_website on Vercel).
// Source HTML lives at sy_website/web/public/youtube-embed.html.
const YT_PROXY_URL = "https://hub.stayyoung.live/youtube-embed.html";
const YT_PROXY_ORIGIN = "https://hub.stayyoung.live";

interface YTProxyTimeMessage {
  __sy: "youtube";
  type: "time";
  currentTime: number;
  duration: number;
}
interface YTProxyStateMessage {
  __sy: "youtube";
  type: "state";
  state: "unstarted" | "ended" | "playing" | "paused" | "buffering" | "cued" | "unknown";
  duration: number;
}
interface YTProxyReadyMessage {
  __sy: "youtube";
  type: "ready";
  duration: number;
}
interface YTProxyErrorMessage {
  __sy: "youtube";
  type: "error";
  code: number;
}
type YTProxyMessage =
  | YTProxyTimeMessage
  | YTProxyStateMessage
  | YTProxyReadyMessage
  | YTProxyErrorMessage;

interface VimeoRef {
  id: string;
  hash?: string;
}

function getVimeoRef(url: string): VimeoRef | null {
  // Order matters: try player.vimeo.com first so we don't match the bare
  // /video/{id} as the unlisted-hash on a vimeo.com path.
  const playerMatch = url.match(/player\.vimeo\.com\/video\/(\d+)(?:\?[^#]*\bh=([a-zA-Z0-9]+))?/);
  if (playerMatch) {
    return playerMatch[2] ? { id: playerMatch[1], hash: playerMatch[2] } : { id: playerMatch[1] };
  }
  const bareMatch = url.match(/vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/);
  if (bareMatch) {
    return bareMatch[2] ? { id: bareMatch[1], hash: bareMatch[2] } : { id: bareMatch[1] };
  }
  return null;
}

function isVimeoUrl(url: string): boolean {
  return url.includes('vimeo.com');
}

function getYouTubeId(url: string): string | null {
  // Supports: youtube.com/watch?v=ID, youtu.be/ID, youtube.com/shorts/ID,
  // youtube.com/embed/ID, m.youtube.com/*, with any trailing query/fragment.
  const patterns = [
    /[?&]v=([a-zA-Z0-9_-]{6,})/,
    /youtu\.be\/([a-zA-Z0-9_-]{6,})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{6,})/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{6,})/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

function isYouTubeUrl(url: string): boolean {
  return /(?:^|\/\/)(?:www\.|m\.)?(?:youtube\.com|youtu\.be)\//.test(url);
}

function isLividUrl(url: string): boolean {
  return /(?:^|\/\/)(?:www\.)?livid\.com\//.test(url);
}

function getLividSlug(url: string): string | null {
  const m = url.match(/livid\.com\/(?:watch|embed)\/([A-Za-z0-9_-]+)/);
  return m ? m[1] : null;
}

// ── Watch-depth milestones ───────────────────────────────────────
// Fires `video_progress` once per 25/50/75/95% milestone per video load, so we
// can see *where* viewers drop off (the existing video_play_started / completion
// pair only tells us start-and-finish). The payload mirrors video_play_started
// (source + provider id) so the two events join cleanly in PostHog.
// NOTE: position-based, not unique-seconds — seeking past a milestone fires it.
// That's intended for a drop-off funnel; don't conflate with the watchTracker
// completion signal (which is anti-seek). Milestones fire once per load, so a
// loop/replay won't re-fire them.
const VIDEO_PROGRESS_MILESTONES = [25, 50, 75, 95] as const;

function useVideoProgressMilestones(
  source: "youtube" | "vimeo" | "livid",
  videoId: string,
) {
  const firedRef = useRef<Set<number>>(new Set());
  // Reset when the underlying video changes (players are reused across days).
  useEffect(() => {
    firedRef.current = new Set();
  }, [source, videoId]);

  return useCallback(
    (percent: number, secondsWatched: number) => {
      if (!Number.isFinite(percent)) return;
      const idKey =
        source === "youtube" ? "youtube_id" : source === "vimeo" ? "vimeo_id" : "livid_slug";
      for (const milestone of VIDEO_PROGRESS_MILESTONES) {
        if (percent >= milestone && !firedRef.current.has(milestone)) {
          firedRef.current.add(milestone);
          logInfo("feature", "video_progress", {
            source,
            [idKey]: videoId,
            percent: milestone,
            seconds_watched: Number.isFinite(secondsWatched) ? Math.round(secondsWatched) : 0,
          });
        }
      }
    },
    [source, videoId],
  );
}

const ChallengeVideoPlayer = forwardRef<ChallengeVideoPlayerHandle, ChallengeVideoPlayerProps>(({
  videoUrl,
  thumbnailUrl,
  onProgress,
  onPlay,
  onComplete,
  onEnded,
  className
}, ref) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [isPiP, setIsPiP] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const hideControlsTimeout = useRef<ReturnType<typeof setTimeout>>();
  // Real-watch tracker: fires onComplete once enough unique seconds were
  // actually played (50% of a clip up to 30s, 60% of anything longer), or at
  // 10% when the player reports `ended` or a looping clip wraps around — so
  // seek-to-end doesn't fake completion. Rules: createWatchTracker.
  const watchTracker = useWatchTracker(onComplete);

  // A video that couldn't load (offline, broken file, YouTube error) used to
  // leave a black box. Say so, with a retry that remounts the player. Being
  // offline is the one failure we can see for the cross-origin iframes too.
  const [loadFailed, setLoadFailed] = useState(false);
  const [offline, setOffline] = useState(() => typeof navigator !== "undefined" && navigator.onLine === false);
  const [retryKey, setRetryKey] = useState(0);
  useEffect(() => {
    const onOnline = () => setOffline(false);
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, []);
  const retryLoad = useCallback(() => {
    setLoadFailed(false);
    setOffline(typeof navigator !== "undefined" && navigator.onLine === false);
    setRetryKey((k) => k + 1);
  }, []);

  const SPEED_OPTIONS = [1, 1.25, 1.5, 1.75, 2];

  // For Vimeo we always use the official Vimeo Player SDK below — it gives
  // us reliable `play`/`timeupdate`/`ended` events across browsers and mobile
  // (which the native <video> + raw MP4 path silently dropped on some setups,
  // causing completion never to fire).
  const isVimeo = isVimeoUrl(videoUrl);
  const vimeoRef = isVimeo ? getVimeoRef(videoUrl) : null;
  const lividSlug = isLividUrl(videoUrl) ? getLividSlug(videoUrl) : null;

  const pipSupported = typeof document !== 'undefined' && document.pictureInPictureEnabled;

  // No MP4 resolution needed anymore — kept as no-ops to preserve render flow.
  const finalVideoUrl = videoUrl;
  const finalThumb = thumbnailUrl;
  const useVimeoSdk = isVimeo && !!vimeoRef;

  const requestPiP = useCallback(async () => {
    if (!videoRef.current || !pipSupported) return;
    try {
      if (!document.pictureInPictureElement) {
        await videoRef.current.requestPictureInPicture();
      }
    } catch (e) {
      console.warn("PiP request failed:", e);
    }
  }, [pipSupported]);

  // Filled by whichever iframe player is mounted; null for the native <video>.
  const iframePauseRef = useRef<(() => void) | null>(null);

  useImperativeHandle(ref, () => ({
    requestPiP,
    isPlaying: () => isPlaying,
    pause: () => {
      if (videoRef.current) {
        videoRef.current.pause();
        setIsPlaying(false);
      }
      try {
        iframePauseRef.current?.();
      } catch {
        // The embed is gone or not ready: nothing to pause.
      }
    },
  }), [requestPiP, isPlaying]);

  const togglePlay = async () => {
    if (!videoRef.current) return;
    try {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        await videoRef.current.play();
        setIsPlaying(true);
        onPlay?.();
      }
    } catch {
      // Sync state with actual video state after play/pause race
      const paused = videoRef.current?.paused;
      if (paused !== undefined) {
        setIsPlaying(!paused);
      }
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        videoRef.current.requestFullscreen();
      }
    }
  };

  const togglePiP = async () => {
    if (!videoRef.current || !pipSupported) return;
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else {
        await videoRef.current.requestPictureInPicture();
      }
    } catch (e) {
      console.warn("PiP toggle failed:", e);
    }
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;

    const currentProgress = (v.currentTime / v.duration) * 100;
    setProgress(currentProgress);
    onProgress?.(currentProgress);

    watchTracker.record(v.currentTime, v.duration);
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!videoRef.current || !isFinite(videoRef.current.duration)) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * videoRef.current.duration;
    if (isFinite(newTime)) {
      videoRef.current.currentTime = newTime;
    }
  };

  const handleProgressTouch = (e: React.TouchEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!videoRef.current || !isFinite(videoRef.current.duration) || e.touches.length === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const touchX = e.touches[0].clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, touchX / rect.width));
    const newTime = percentage * videoRef.current.duration;
    if (isFinite(newTime)) {
      videoRef.current.currentTime = newTime;
    }
  };

  const cycleSpeed = () => {
    if (!videoRef.current) return;
    const currentIndex = SPEED_OPTIONS.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % SPEED_OPTIONS.length;
    const newRate = SPEED_OPTIONS[nextIndex];
    videoRef.current.playbackRate = newRate;
    setPlaybackRate(newRate);
  };

  const skip = (seconds: number) => {
    if (!videoRef.current || !isFinite(videoRef.current.duration)) return;
    videoRef.current.currentTime = Math.max(0, Math.min(videoRef.current.duration, videoRef.current.currentTime + seconds));
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (hideControlsTimeout.current) clearTimeout(hideControlsTimeout.current);
    hideControlsTimeout.current = setTimeout(() => {
      if (isPlaying) setShowControls(false);
    }, 3000);
  };

  // Cleanup timeout
  useEffect(() => {
    return () => {
      if (hideControlsTimeout.current) clearTimeout(hideControlsTimeout.current);
    };
  }, []);

  // Reset watch tracking when the video URL changes (next-day navigation reuses
  // the same player instance with a new URL).
  useEffect(() => {
    watchTracker.reset();
  }, [videoUrl, watchTracker]);

  // Sync PiP state via events
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onEnterPiP = () => setIsPiP(true);
    const onLeavePiP = () => setIsPiP(false);

    video.addEventListener("enterpictureinpicture", onEnterPiP);
    video.addEventListener("leavepictureinpicture", onLeavePiP);
    return () => {
      video.removeEventListener("enterpictureinpicture", onEnterPiP);
      video.removeEventListener("leavepictureinpicture", onLeavePiP);
    };
  }, [finalVideoUrl]);

  // Auto-PiP when video leaves viewport while playing
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !pipSupported) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting && isPlaying && videoRef.current && !document.pictureInPictureElement) {
          videoRef.current.requestPictureInPicture().catch(() => {});
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, [isPlaying, pipSupported]);

  // Early return after all hooks
  if (!videoUrl || videoUrl.trim() === '') return null;

  if (offline || loadFailed) {
    return <VideoUnavailable className={className} onRetry={retryLoad} offline={offline} />;
  }

  // YouTube — uses the IFrame Player API so we can track real watch time.
  // The native <video> element can't play YouTube directly.
  if (isYouTubeUrl(videoUrl)) {
    const youTubeId = getYouTubeId(videoUrl);
    if (youTubeId) {
      return (
        <YouTubeChallengePlayer
          key={retryKey}
          onError={() => setLoadFailed(true)}
          videoUrl={videoUrl}
          youTubeId={youTubeId}
          onPlay={onPlay}
          onComplete={onComplete}
          onEnded={onEnded}
          onProgress={onProgress}
          className={className}
          pauseRef={iframePauseRef}
        />
      );
    }
  }

  // Livid: embed player implements the embedly player.js v2.0 protocol, so we
  // attach the open-source player.js client to the iframe for the same
  // play/timeupdate/ended tracking we get from Vimeo's SDK.
  if (lividSlug) {
    return (
      <LividIframePlayer
        key={retryKey}
        slug={lividSlug}
        onPlay={onPlay}
        onComplete={onComplete}
        onEnded={onEnded}
        onProgress={onProgress}
        className={className}
        pauseRef={iframePauseRef}
      />
    );
  }

  // Vimeo: always render via the Vimeo Player SDK (reliable cross-browser
  // play/timeupdate/ended events). Replaces the old "resolve to MP4 + native
  // <video>" path which silently dropped completion on some setups.
  if (useVimeoSdk && vimeoRef) {
    return (
      <VimeoIframeFallbackPlayer
        key={retryKey}
        vimeoId={vimeoRef.id}
        vimeoHash={vimeoRef.hash}
        onPlay={onPlay}
        onComplete={onComplete}
        onEnded={onEnded}
        onProgress={onProgress}
        className={className}
        pauseRef={iframePauseRef}
      />
    );
  }

  if (!finalVideoUrl) return null;

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative aspect-video bg-black rounded-lg overflow-hidden group",
        className
      )}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => isPlaying && setShowControls(false)}
    >
      <video
        key={retryKey}
        ref={videoRef}
        src={finalVideoUrl}
        onError={() => setLoadFailed(true)}
        poster={finalThumb || undefined}
        playsInline
        className="w-full h-full object-contain"
        onTimeUpdate={handleTimeUpdate}
        onEnded={() => {
          const v = videoRef.current;
          if (v) watchTracker.checkEnded(v.duration);
          onEnded?.();
        }}
      />

      {/* Play/pause overlay — only when paused */}
      {!isPlaying && (
        <button
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity active:bg-black/40"
          aria-label="ניגון"
        >
          <div className="w-20 h-20 rounded-full bg-primary/90 flex items-center justify-center active:bg-primary transition-colors">
            <Play className="h-10 w-10 text-primary-foreground me-1" />
          </div>
        </button>
      )}

      {/* Tap overlay for playing state — toggles play/pause */}
      {isPlaying && (
        <button
          onClick={togglePlay}
          className="absolute inset-0 z-0"
          aria-label="השהיה"
        />
      )}

      {/* Controls */}
      <div
        onClick={e => e.stopPropagation()}
        onTouchStart={e => e.stopPropagation()}
        className={cn(
          "absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 transition-opacity duration-300 z-10",
          showControls ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
      >
        {/* Progress bar */}
        <div
          dir="ltr"
          className="w-full h-2 bg-white/30 rounded-full cursor-pointer mb-3"
          onClick={handleProgressClick}
          onTouchStart={handleProgressTouch}
        >
          <div
            className="h-full bg-primary rounded-full transition-all relative"
            style={{ width: `${progress}%` }}
          >
            <div className="absolute left-full top-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full shadow-sm" />
          </div>
        </div>

        {/* Control buttons */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={togglePlay}
            className="tap-target [--tap-size:40px] h-8 w-8 text-white active:bg-white/20"
            aria-label={isPlaying ? "השהיה" : "ניגון"}
          >
            {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => skip(-10)}
            className="tap-target [--tap-size:40px] h-8 w-8 text-white active:bg-white/20"
            aria-label="10 שניות אחורה"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => skip(10)}
            className="tap-target [--tap-size:40px] h-8 w-8 text-white active:bg-white/20"
            aria-label="10 שניות קדימה"
          >
            <RotateCw className="h-4 w-4" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={toggleMute}
            className="tap-target [--tap-size:40px] h-8 w-8 text-white active:bg-white/20"
            aria-label={isMuted ? "ביטול השתקה" : "השתקה"}
          >
            {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
          </Button>

          <Button
            variant="ghost"
            onClick={cycleSpeed}
            className="tap-target [--tap-size:40px] h-8 px-2 text-white active:bg-white/20 text-xs font-mono min-w-[3rem]"
            aria-label={`מהירות ניגון ${playbackRate}x`}
          >
            x{playbackRate}
          </Button>

          <div className="flex-1" />

          {pipSupported && (
            <Button
              variant="ghost"
              size="icon"
              onClick={togglePiP}
              className={cn("tap-target [--tap-size:40px] h-8 w-8 text-white active:bg-white/20", isPiP && "bg-white/20")}
              aria-label="תמונה בתוך תמונה"
            >
              <PictureInPicture2 className="h-5 w-5" />
            </Button>
          )}

          <Button
            variant="ghost"
            size="icon"
            onClick={toggleFullscreen}
            className="tap-target [--tap-size:40px] h-8 w-8 text-white active:bg-white/20"
            aria-label="מסך מלא"
          >
            <Maximize className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
});

ChallengeVideoPlayer.displayName = "ChallengeVideoPlayer";

function VideoUnavailable({ className, onRetry, offline }: { className?: string; onRetry: () => void; offline: boolean }) {
  return (
    <div
      dir="rtl"
      className={cn("aspect-video bg-muted-warm rounded-lg flex flex-col items-center justify-center gap-3 px-6 text-center", className)}
    >
      <p className="font-semibold text-foreground">
        {offline ? "אין חיבור לאינטרנט" : "לא הצלחנו לטעון את הסרטון"}
      </p>
      <p className="text-sm text-muted-foreground">
        {offline ? "הסרטון ייטען כשהחיבור יחזור." : "בדקו את החיבור לאינטרנט ונסו שוב."}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="min-h-11 rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground active:scale-[0.98] transition-transform"
      >
        לנסות שוב
      </button>
    </div>
  );
}

interface YouTubeChallengePlayerProps {
  videoUrl: string;
  youTubeId: string;
  onPlay?: () => void;
  onComplete?: () => void;
  onEnded?: () => void;
  onProgress?: (progress: number) => void;
  className?: string;
  /** The proxy reported a player error (unavailable, embedding off, network). */
  onError?: () => void;
  pauseRef?: PauseSlot;
}

function YouTubeChallengePlayer({
  videoUrl,
  youTubeId,
  onPlay,
  onComplete,
  onEnded,
  onProgress,
  className,
  onError,
  pauseRef,
}: YouTubeChallengePlayerProps) {
  const watchTracker = useWatchTracker(onComplete);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Parent → proxy command (see sy_website/web/public/youtube-embed.html).
  useEffect(() => {
    if (!pauseRef) return;
    const pause = () => {
      iframeRef.current?.contentWindow?.postMessage({ __sy: "youtube:cmd", cmd: "pause" }, YT_PROXY_ORIGIN);
    };
    pauseRef.current = pause;
    return () => {
      if (pauseRef.current === pause) pauseRef.current = null;
    };
  }, [pauseRef]);

  // Latest callbacks via refs so the message handler isn't rebuilt on parent re-renders.
  const onPlayRef = useRef(onPlay);
  const onProgressRef = useRef(onProgress);
  const onErrorRef = useRef(onError);
  onPlayRef.current = onPlay;
  onProgressRef.current = onProgress;
  onErrorRef.current = onError;
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;
  const playLoggedRef = useRef(false);

  const reportMilestone = useVideoProgressMilestones("youtube", youTubeId);
  const reportMilestoneRef = useRef(reportMilestone);
  reportMilestoneRef.current = reportMilestone;

  // Shorts are vertical (9:16); standard videos are 16:9.
  const isShorts = /youtube\.com\/shorts\//.test(videoUrl);

  const src = useMemo(() => {
    const params = new URLSearchParams({
      v: youTubeId,
      parent: window.location.origin,
    });
    return `${YT_PROXY_URL}?${params.toString()}`;
  }, [youTubeId]);

  useEffect(() => {
    watchTracker.reset();
    playLoggedRef.current = false;

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== YT_PROXY_ORIGIN) return;
      const msg = event.data as YTProxyMessage | undefined;
      if (!msg || msg.__sy !== "youtube") return;

      if (msg.type === "state") {
        if (msg.state === "playing") {
          if (!playLoggedRef.current) {
            playLoggedRef.current = true;
            logInfo("feature", "video_play_started", {
              source: "youtube",
              youtube_id: youTubeId,
            });
          }
          onPlayRef.current?.();
        } else if (msg.state === "ended") {
          // Honor real ENDED only if the user actually watched enough.
          watchTracker.checkEnded(msg.duration);
          onEndedRef.current?.();
        }
      } else if (msg.type === "time") {
        const { currentTime, duration } = msg;
        if (Number.isFinite(currentTime) && Number.isFinite(duration) && duration >= 1) {
          const percent = (currentTime / duration) * 100;
          onProgressRef.current?.(percent);
          reportMilestoneRef.current(percent, currentTime);
          // Loop-aware: YouTube Shorts auto-loop without firing ENDED.
          watchTracker.recordWithLoopDetection(currentTime, duration);
        }
      } else if (msg.type === "error") {
        console.warn("YouTube proxy error:", msg.code);
        onErrorRef.current?.();
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [youTubeId, watchTracker]);

  return (
    <div
      className={cn(
        "relative bg-black rounded-lg overflow-hidden",
        isShorts ? "w-full max-w-[320px] aspect-[9/16] mx-auto" : "aspect-video",
        className,
      )}
    >
      <iframe
        ref={iframeRef}
        src={src}
        className="w-full h-full border-0"
        title="סרטון יוטיוב"
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen; clipboard-write"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
      />
    </div>
  );
}

interface VimeoIframeFallbackPlayerProps {
  vimeoId: string;
  vimeoHash?: string;
  onPlay?: () => void;
  onComplete?: () => void;
  onEnded?: () => void;
  onProgress?: (progress: number) => void;
  className?: string;
  pauseRef?: PauseSlot;
}

// Minimal Vimeo Player SDK typings — avoids pulling @types/vimeo__player.
interface VimeoPlayerInstance {
  on: (event: string, handler: (data?: { duration?: number; seconds?: number; percent?: number }) => void) => void;
  getDuration: () => Promise<number>;
  getMuted: () => Promise<boolean>;
  setMuted: (muted: boolean) => Promise<boolean>;
  getVolume: () => Promise<number>;
  pause: () => Promise<void>;
  destroy: () => Promise<void>;
}
interface VimeoNamespace {
  Player: new (
    target: HTMLElement | HTMLIFrameElement | string,
    options?: Record<string, unknown>,
  ) => VimeoPlayerInstance;
}
declare global {
  interface Window {
    Vimeo?: VimeoNamespace;
  }
}

let vimeoSdkPromise: Promise<VimeoNamespace> | null = null;
function loadVimeoSdk(): Promise<VimeoNamespace> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Vimeo SDK requires window"));
  }
  if (window.Vimeo?.Player) return Promise.resolve(window.Vimeo);
  if (vimeoSdkPromise) return vimeoSdkPromise;

  vimeoSdkPromise = new Promise<VimeoNamespace>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-vimeo-sdk="1"]');
    if (existing) {
      existing.addEventListener("load", () => {
        if (window.Vimeo?.Player) resolve(window.Vimeo);
        else reject(new Error("Vimeo SDK loaded without Player"));
      });
      existing.addEventListener("error", () => reject(new Error("Vimeo SDK failed to load")));
      return;
    }
    const tag = document.createElement("script");
    tag.src = "https://player.vimeo.com/api/player.js";
    tag.async = true;
    tag.dataset.vimeoSdk = "1";
    tag.onload = () => {
      if (window.Vimeo?.Player) resolve(window.Vimeo);
      else reject(new Error("Vimeo SDK loaded without Player"));
    };
    tag.onerror = () => reject(new Error("Vimeo SDK failed to load"));
    document.head.appendChild(tag);
  });
  return vimeoSdkPromise;
}

function VimeoIframeFallbackPlayer({
  vimeoId,
  vimeoHash,
  onPlay,
  onComplete,
  onEnded,
  onProgress,
  className,
  pauseRef,
}: VimeoIframeFallbackPlayerProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const playerRef = useRef<VimeoPlayerInstance | null>(null);

  // Through the SDK when it attached; otherwise the embed's own postMessage
  // command, which works even when the SDK script failed to load.
  useEffect(() => {
    if (!pauseRef) return;
    const pause = () => {
      const player = playerRef.current;
      if (player) {
        player.pause().catch(() => {});
        return;
      }
      iframeRef.current?.contentWindow?.postMessage(JSON.stringify({ method: "pause" }), "https://player.vimeo.com");
    };
    pauseRef.current = pause;
    return () => {
      if (pauseRef.current === pause) pauseRef.current = null;
    };
  }, [pauseRef]);
  const watchTracker = useWatchTracker(onComplete);
  const lastDurationRef = useRef(0);
  const playLoggedRef = useRef(false);

  const onPlayRef = useRef(onPlay);
  const onProgressRef = useRef(onProgress);
  const onEndedRef = useRef(onEnded);
  onPlayRef.current = onPlay;
  onProgressRef.current = onProgress;
  onEndedRef.current = onEnded;

  const reportMilestone = useVideoProgressMilestones("vimeo", vimeoId);
  const reportMilestoneRef = useRef(reportMilestone);
  reportMilestoneRef.current = reportMilestone;

  // The iframe renders the video on its own (so the user sees it even if the
  // SDK script is blocked by CSP/network). The SDK then attaches to the
  // existing iframe purely for play/timeupdate/ended event tracking.
  // The h= param is required for unlisted videos with privacy: "disable".
  const hashParam = vimeoHash ? `&h=${vimeoHash}` : "";
  const embedSrc = `https://player.vimeo.com/video/${vimeoId}?title=0&byline=0&portrait=0&playsinline=1${hashParam}`;

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    let cancelled = false;
    watchTracker.reset();

    loadVimeoSdk()
      .then((Vimeo) => {
        if (cancelled) return;

        const player = new Vimeo.Player(iframe);
        playerRef.current = player;

        player.on("play", () => {
          onPlayRef.current?.();
          // Force-unmute if iframe loaded muted (browser autoplay policy or
          // Vimeo persisting muted=true in localStorage across plays).
          if (playLoggedRef.current) return;
          playLoggedRef.current = true;
          Promise.all([player.getMuted(), player.getVolume()])
            .then(([mutedBefore, volume]) => {
              if (mutedBefore) player.setMuted(false).catch(() => {});
              logInfo("feature", "video_play_started", {
                source: "vimeo",
                vimeo_id: vimeoId,
                muted_before: mutedBefore,
                volume,
              });
            })
            .catch(() => {
              logInfo("feature", "video_play_started", {
                source: "vimeo",
                vimeo_id: vimeoId,
                state_unavailable: true,
              });
            });
        });

        player.on("timeupdate", (data) => {
          const seconds = data?.seconds;
          const duration = data?.duration;
          if (typeof seconds !== "number" || typeof duration !== "number") return;
          if (duration >= 1) {
            lastDurationRef.current = duration;
            const percent = (seconds / duration) * 100;
            onProgressRef.current?.(percent);
            reportMilestoneRef.current(percent, seconds);
          }
          // Loop-aware: if a Vimeo clip loops we still credit the user.
          watchTracker.recordWithLoopDetection(seconds, duration);
        });

        player.on("ended", (data) => {
          onEndedRef.current?.();
          const duration =
            typeof data?.duration === "number" && data.duration > 0
              ? data.duration
              : lastDurationRef.current;
          if (duration < 1) {
            player.getDuration().then((d) => {
              watchTracker.checkEnded(d || lastDurationRef.current);
            }).catch(() => watchTracker.checkEnded(lastDurationRef.current));
            return;
          }
          watchTracker.checkEnded(duration);
        });
      })
      .catch((err) => {
        // SDK failed to load — the iframe still plays the video, we just lose
        // automatic completion tracking. The user can still watch normally.
        console.warn("[Vimeo] SDK load failed (video will still play):", err);
      });

    return () => {
      cancelled = true;
      const player = playerRef.current;
      playerRef.current = null;
      if (player) {
        player.destroy().catch(() => {});
      }
    };
  }, [vimeoId, vimeoHash, watchTracker]);

  return (
    <div
      className={cn(
        "relative aspect-video bg-black rounded-lg overflow-hidden",
        className,
      )}
    >
      <iframe
        ref={iframeRef}
        src={embedSrc}
        className="w-full h-full"
        frameBorder={0}
        allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
        allowFullScreen
        title="Vimeo video"
      />
    </div>
  );
}

interface LividIframePlayerProps {
  slug: string;
  onPlay?: () => void;
  onComplete?: () => void;
  onEnded?: () => void;
  onProgress?: (percent: number) => void;
  className?: string;
  pauseRef?: PauseSlot;
}

function LividIframePlayer({
  slug,
  onPlay,
  onComplete,
  onEnded,
  onProgress,
  className,
  pauseRef,
}: LividIframePlayerProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const watchTracker = useWatchTracker(onComplete);
  const lastDurationRef = useRef(0);
  const lastSecondsRef = useRef(0);
  const playLoggedRef = useRef(false);

  const onPlayRef = useRef(onPlay);
  const onProgressRef = useRef(onProgress);
  const onEndedRef = useRef(onEnded);
  onPlayRef.current = onPlay;
  onProgressRef.current = onProgress;
  onEndedRef.current = onEnded;

  const reportMilestone = useVideoProgressMilestones("livid", slug);
  const reportMilestoneRef = useRef(reportMilestone);
  reportMilestoneRef.current = reportMilestone;

  // dnt=1 keeps Livid's view analytics off; the inner <video> plays inline on
  // iOS. The iframe renders the video itself even if the player.js client is
  // blocked — we just lose automatic completion tracking in that case.
  const embedSrc = `https://livid.com/embed/${slug}?dnt=1`;

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    let cancelled = false;
    watchTracker.reset();
    lastSecondsRef.current = 0;

    const player = new playerjs.Player(iframe);

    // player.js `pause` is a core method of the protocol; the client queues it
    // until the embed reports ready.
    const pause = () => player.pause();
    if (pauseRef) pauseRef.current = pause;

    player.on("ready", () => {
      if (cancelled) return;

      player.on("play", () => {
        onPlayRef.current?.();
        if (playLoggedRef.current) return;
        playLoggedRef.current = true;
        logInfo("feature", "video_play_started", { source: "livid", livid_slug: slug });
      });

      player.on("timeupdate", (data) => {
        const seconds = data?.seconds;
        const duration = data?.duration;
        if (typeof seconds !== "number" || typeof duration !== "number") return;
        if (duration >= 1) {
          lastDurationRef.current = duration;
          const percent = (seconds / duration) * 100;
          onProgressRef.current?.(percent);
          reportMilestoneRef.current(percent, seconds);
        }
        // Range-fill between ticks: mobile WebView throttles player.js to a
        // sparse cadence, so crediting only the single tick-second can leave
        // the unique-seconds Set short of the completion ratio on an honest
        // full watch. A small forward gap (not a seek) credits every whole
        // second passed through; the tick itself stays loop-aware.
        const prev = lastSecondsRef.current;
        lastSecondsRef.current = seconds;
        recordTickWithGapFill(watchTracker, prev, seconds, duration);
      });

      player.on("ended", () => {
        onEndedRef.current?.();
        // player.js `ended` carries no payload — use the last observed duration,
        // or ask the player (callback API) if we never received a timeupdate.
        if (lastDurationRef.current >= 1) {
          watchTracker.checkEnded(lastDurationRef.current);
          return;
        }
        player.getDuration((d) => watchTracker.checkEnded(d || lastDurationRef.current));
      });
    });

    return () => {
      cancelled = true;
      if (pauseRef && pauseRef.current === pause) pauseRef.current = null;
      try {
        player.off("play");
        player.off("timeupdate");
        player.off("ended");
      } catch {
        /* client already torn down */
      }
    };
  }, [slug, watchTracker, pauseRef]);

  return (
    <div className={cn("relative aspect-video bg-black rounded-lg overflow-hidden", className)}>
      <iframe
        ref={iframeRef}
        src={embedSrc}
        className="w-full h-full"
        frameBorder={0}
        allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
        allowFullScreen
        title="Livid video"
      />
    </div>
  );
}

export default ChallengeVideoPlayer;
